import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import css from './SubscriptionHistory.css';

const TRANSITION_CONFIG = {
  payment: {
    labelId: 'SubscriptionHistory.paymentLabel',
    descId: 'SubscriptionHistory.paymentDesc',
    color: '#3643b9',
  },
  pickup: {
    labelId: 'SubscriptionHistory.pickupLabel',
    descId: 'SubscriptionHistory.pickupDesc',
    color: '#0f9263',
  },
  refund: {
    labelId: 'SubscriptionHistory.refundLabel',
    descId: 'SubscriptionHistory.refundDesc',
    color: '#e74c3c',
  },
  cancel: {
    labelId: 'SubscriptionHistory.cancelLabel',
    descId: 'SubscriptionHistory.cancelDesc',
    color: '#e74c3c',
  },
  complete: {
    labelId: 'SubscriptionHistory.completeLabel',
    descId: 'SubscriptionHistory.completeDesc',
    color: '#475467',
  },
  'payment-method-changed': {
    labelId: 'SubscriptionHistory.paymentMethodChangedLabel',
    descId: 'SubscriptionHistory.paymentMethodChangedDesc',
    color: '#2b6cb0',
  },
  'ui-expired': {
    labelId: 'SubscriptionHistory.expireLabel',
    descId: 'SubscriptionHistory.expireDesc',
    color: '#d9383a',
  },
  'ui-payment-error': {
    labelId: 'SubscriptionHistory.errorLabel',
    descId: 'SubscriptionHistory.errorDescWithRetryAndUnpaid',
    color: '#d9383a',
  },
  default: {
    labelId: 'SubscriptionHistory.defaultLabel',
    descId: 'SubscriptionHistory.defaultDesc',
    color: '#a3acb9',
  },
};

const SubscriptionHistory = ({
  transitions = [],
  documents = [],
  currency,
  intl,
  processState,
  unpaidSince,
  nextPaymentAttemptAt,
  updatedAt,
}) => {
  let allTransitions = [...transitions];

  const isExpired = processState === 'payment-expired';
  const hasUnpaidIssue = processState === 'active' && !!unpaidSince;

  if (isExpired) {
    allTransitions.push({
      code: 'ui-expired',
      createdAt: updatedAt || new Date().toISOString(),
      amount: null,
    });
  }

  if (hasUnpaidIssue) {
    allTransitions.push({
      code: 'ui-payment-error',
      createdAt: unpaidSince,
      amount: null,
    });
  }

  const firstPaymentTransition = [...transitions]
    .filter(t => t.code === 'payment')
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];

  const sortedTransitions = [...allTransitions].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <div className={css.historyList}>
      {sortedTransitions.map((t, idx) => {
        const isError = t.code === 'ui-payment-error' || t.code === 'ui-expired';
        const isPayment = t.code === 'payment';
        const isRefund = t.code === 'refund';
        const isFirstPayment = isPayment && t === firstPaymentTransition;
        const config = TRANSITION_CONFIG[t.code] || TRANSITION_CONFIG['default'];

        // Identyfikator płatności
        const paymentId = t.paymentId || t.id;

        // Szukamy faktury powiązanej z daną płatnością
        const invoice = isPayment && paymentId
          ? documents.find(doc => doc.paymentId === paymentId && doc.type === 'invoice')
          : null;

        // Szukamy korekty faktury powiązanej ze zdarzeniem refund/cancel po paymentId LUB wybieramy dowolną korektę dla zdarzeń zwrotu
        const correctionInvoice = isRefund
          ? (documents.find(doc => doc.paymentId === paymentId && doc.type === 'invoice-correction') ||
             documents.find(doc => doc.type === 'invoice-correction'))
          : null;

        let descId = config.descId;
        if (isFirstPayment) {
          descId = 'SubscriptionHistory.initialPaymentDesc';
        } else if (t.code === 'ui-payment-error' && !nextPaymentAttemptAt) {
          descId = 'SubscriptionHistory.errorDesc';
        }

        const isLast = idx === sortedTransitions.length - 1;

        const hasPeriod = isPayment && t.period;
        const formattedPeriod = hasPeriod ? (
          <span className={css.periodInfo}>
            <FormattedMessage
              id="SubscriptionHistory.periodText"
              values={{
                start: intl.formatDate(t.period.start, { day: 'numeric', month: 'short' }),
                end: intl.formatDate(t.period.end, { day: 'numeric', month: 'short', year: 'numeric' })
              }}
            />
          </span>
        ) : null;

        const messageValues = {
          attemptDate: t.createdAt ? <strong>{intl.formatDate(t.createdAt)}</strong> : null,
          unpaidSinceDate: unpaidSince ? <strong>{intl.formatDate(unpaidSince)}</strong> : null,
          nextAttemptDate: nextPaymentAttemptAt ? <strong>{intl.formatDate(nextPaymentAttemptAt)}</strong> : null,
          amount: t.amount ? <strong>{formatMoney(intl, { amount: t.amount, currency })}</strong> : null,
          last4: t.cardLast4 ? <strong>•••• {t.cardLast4}</strong> : null,
        };

        return (
          <div key={`${t.code}-${idx}`} className={classNames(css.historyItem, { [css.errorItem]: isError })}>
            <div className={css.historyMarker}>
              <div className={css.dot} style={{ backgroundColor: config.color }} />
              {!isLast && <div className={css.line} />}
            </div>

            <div className={css.historyContent}>
              <div className={css.historyHeader}>
                <span className={css.historyLabel}>
                  <FormattedMessage id={config.labelId} />
                </span>
                <span className={css.historyTimestamp}>
                  {intl.formatDate(t.createdAt, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}, {intl.formatTime(t.createdAt)}
                </span>
              </div>

              <p className={css.historyDescription}>
                <FormattedMessage id={descId} values={messageValues} />
              </p>

              {formattedPeriod}

              {/* Przycisk pobrania faktury */}
              {invoice && (
                <div className={css.invoiceWrapper}>
                  <a
                    href={invoice.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.invoiceLink}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="12" y1="18" x2="12" y2="12"></line>
                      <polyline points="9 15 12 18 15 15"></polyline>
                    </svg>
                    <FormattedMessage
                      id="SubscriptionHistory.downloadInvoice"
                      defaultValue="Faktura {name}"
                      values={{ name: invoice.name }}
                    />
                  </a>
                </div>
              )}

              {correctionInvoice && (
                <div className={css.invoiceWrapper}>
                  <a
                    href={correctionInvoice.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.invoiceLink}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="12" y1="18" x2="12" y2="12"></line>
                      <polyline points="9 15 12 18 15 15"></polyline>
                    </svg>
                    <FormattedMessage
                      id="SubscriptionHistory.downloadCorrection"
                      defaultValue="Korekta faktury {name}"
                      values={{ name: correctionInvoice.name }}
                    />
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SubscriptionHistory;